import { useState, KeyboardEvent, useCallback } from 'react';
import mixpanel from 'mixpanel-browser';
import i18n from 'i18next';

import { Icon, Popup, Tooltip } from 'web-components';
import { Flex, Text, SVG, icons } from 'web-styled';
import config from 'web-config';

import * as S from './styled';

// Icon mapping for add actions
const ADD_ICONS = {
  client: SVG.Clients,
  exercise: SVG.Exercise,
  workout: SVG.KettleBell,
  program: SVG.Program,
  video: SVG.VideoLibrary,
  announcement: SVG.Announcement,
} as const;

// Types
interface Action {
  icon: string;
  name: string;
  action: () => void;
}

interface HeaderProps {
  isPro: boolean;
  avatar: string;
  namespace: any; // Consider creating a proper type for this
  searchQuery: string;
  addActions: Action[];
  profileActions: Action[];
  setSearchQuery: (text: string) => void;
  onKeyDown: (event: KeyboardEvent) => void;
}

interface DropdownState {
  menu: boolean;
  help: boolean;
  addActions: boolean;
}

// Custom hook for dropdown state management
const useDropdownState = () => {
  const [dropdownState, setDropdownState] = useState<DropdownState>({
    menu: false,
    help: false,
    addActions: false,
  });

  const toggleDropdown = useCallback((dropdown: keyof DropdownState) => {
    setDropdownState(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown],
    }));
  }, []);

  const closeDropdown = useCallback((dropdown: keyof DropdownState) => {
    setDropdownState(prev => ({
      ...prev,
      [dropdown]: false,
    }));
  }, []);

  const closeAllDropdowns = useCallback(() => {
    setDropdownState({
      menu: false,
      help: false,
      addActions: false,
    });
  }, []);

  return {
    dropdownState,
    toggleDropdown,
    closeDropdown,
    closeAllDropdowns,
  };
};

// Search component
const SearchSection = ({ searchQuery, setSearchQuery, onKeyDown }: {
  searchQuery: string;
  setSearchQuery: (text: string) => void;
  onKeyDown: (event: KeyboardEvent) => void;
}) => (
  <S.Search data-tour="tour__search">
    <S.Input
      placeholder="Search your library"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onKeyDown={onKeyDown}
    />
    <S.SearchButton
      onClick={() => searchQuery && setSearchQuery('')}
    >
      <Icon type={searchQuery ? "fas fa-times" : "fas fa-search"} />
    </S.SearchButton>
  </S.Search>
);

// Add Actions Dropdown
const AddActionsDropdown = ({
  isOpen,
  addActions,
  onToggle,
  onClose
}: {
  isOpen: boolean;
  addActions: Action[];
  onToggle: () => void;
  onClose: () => void;
}) => {
  const handleToggle = useCallback(() => {
    onToggle();
    if (!isOpen) {
      mixpanel.track('Clicked universal add button');
    }
  }, [isOpen, onToggle]);

  const handleActionClick = useCallback((action: Action) => {
    action.action();
    onClose();
  }, [onClose]);

  return (
    <S.Dropdown isOpen={isOpen}>
      <Popup
        isOpen={isOpen}
        trigger={(
          <S.Trigger isOpen={isOpen}>
            <Flex column align="center" spacing={5}>
              <S.PlusCircle isOpen={isOpen} />
              <Text
                color={isOpen ? "#ffffff" : "#757575"}
                size="10px"
                lineHeight="11px"
                whiteSpace="pre-line"
              >
                Add
              </Text>
            </Flex>
          </S.Trigger>
        )}
        setPopupVisible={handleToggle}
        onClickOutside={onClose}
      >
        <S.Actions mr={116} minWidth="180px">
          {addActions.map((action) => {
            const IconComponent = ADD_ICONS[action.icon as keyof typeof ADD_ICONS];
            return (
              <S.Action
                key={action.name}
                onClick={() => handleActionClick(action)}
                align="center"
              >
                <Flex flex="0 0 20px" align="center">
                  <S.SVGIcon>
                    {IconComponent && (
                      <IconComponent fill="#fff" stroke="#fff" />
                    )}
                  </S.SVGIcon>
                </Flex>
                <span>{action.name}</span>
              </S.Action>
            );
          })}
        </S.Actions>
      </Popup>
    </S.Dropdown>
  );
};

// Help Dropdown
const HelpDropdown = ({
  isOpen,
  isPro,
  namespace,
  onToggle,
  onClose
}: {
  isOpen: boolean;
  isPro: boolean;
  namespace: any;
  onToggle: () => void;
  onClose: () => void;
}) => {
  const handleToggle = useCallback(() => {
    onToggle();
    if (!isOpen) {
      mixpanel.track('Clicked help button');
    }
  }, [isOpen, onToggle]);

  const getSupportUrl = () => {
    return (isPro || namespace.symbol === 'gymcloud' || namespace.symbol === 'ideafit')
      ? config.urls.freshdesk
      : config.urls.support;
  };

  return (
    <S.Dropdown data-tour="tour__help" isOpen={isOpen}>
      <Popup
        isOpen={isOpen}
        trigger={(
          <S.Trigger isOpen={isOpen}>
            <Tooltip
              body={i18n.t('tooltip.main.header.help')}
              isOpen={isOpen ? false : undefined}
              positions={['left']}
            >
              <Flex column align="center" spacing={5}>
                <S.HelpIcon />
                <Text
                  color={isOpen ? "#ffffff" : "#757575"}
                  size="10px"
                  lineHeight="11px"
                  whiteSpace="pre-line"
                >
                  Help
                </Text>
              </Flex>
            </Tooltip>
          </S.Trigger>
        )}
        setPopupVisible={handleToggle}
        onClickOutside={onClose}
      >
        <S.Actions mr={76}>
          <S.Action
            as="a"
            rel="noopener noreferrer"
            href="https://gymcloud.freshdesk.com/support/tickets/new"
            target="_blank"
            align="center"
          >
            <Flex flex="0 0 15px">
              <Icon size="14px" type="fas fa-pencil-alt" />
            </Flex>
            <span>Give Feedback</span>
          </S.Action>
          <S.Action
            as="a"
            rel="noopener noreferrer"
            href={getSupportUrl()}
            target="_blank"
            align="center"
          >
            <Flex flex="0 0 15px">
              <img
                src={icons.helpcenter}
                alt="Help Center"
                width="13px"
              />
            </Flex>
            <span>Go to Support Portal</span>
          </S.Action>
        </S.Actions>
      </Popup>
    </S.Dropdown>
  );
};

// Profile Dropdown
const ProfileDropdown = ({
  isOpen,
  isPro,
  avatar,
  namespace,
  profileActions,
  onToggle,
  onClose
}: {
  isOpen: boolean;
  isPro: boolean;
  avatar: string;
  namespace: any;
  profileActions: Action[];
  onToggle: () => void;
  onClose: () => void;
}) => {
  const handleActionClick = useCallback((action: Action) => {
    action.action();
    onClose();
  }, [onClose]);

  const avatarSrc = avatar || namespace.webapp_logo_mark?.url;

  const AvatarComponent = (
    <S.Avatar avatar={avatarSrc} />
  );

  return (
    <S.Dropdown
      width="76px"
      data-tour="tour__profile"
      isOpen={isOpen}
    >
      <Popup
        isOpen={isOpen}
        trigger={(
          <Flex>
            {isPro ? (
              <Tooltip
                body={i18n.t('tooltip.main.header.user')}
                isOpen={isOpen ? false : undefined}
                positions={['left']}
              >
                {AvatarComponent}
              </Tooltip>
            ) : (
              AvatarComponent
            )}
          </Flex>
        )}
        setPopupVisible={onToggle}
        onClickOutside={onClose}
      >
        <S.Actions>
          {profileActions.map(item => (
            <S.Action
              key={item.name}
              onClick={() => handleActionClick(item)}
            >
              <Flex flex="0 0 25%">
                <S.ActionIcon
                  icon={item.icon}
                  style={{
                    backgroundImage: `url(${icons[item.icon]})`,
                  }}
                />
              </Flex>
              <span>{item.name}</span>
            </S.Action>
          ))}
        </S.Actions>
      </Popup>
    </S.Dropdown>
  );
};

// Main Header Component
const Header = ({
  isPro,
  avatar,
  addActions,
  profileActions,
  namespace,
  searchQuery,
  setSearchQuery,
  onKeyDown,
}: HeaderProps) => {
  const { dropdownState, toggleDropdown, closeDropdown } = useDropdownState();

  return (
    <S.Header>
      {isPro && (
        <SearchSection
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onKeyDown={onKeyDown}
        />
      )}

      <Flex margin="0 0 0 auto">
        {isPro && (
          <AddActionsDropdown
            isOpen={dropdownState.addActions}
            addActions={addActions}
            onToggle={() => toggleDropdown('addActions')}
            onClose={() => closeDropdown('addActions')}
          />
        )}

        <HelpDropdown
          isOpen={dropdownState.help}
          isPro={isPro}
          namespace={namespace}
          onToggle={() => toggleDropdown('help')}
          onClose={() => closeDropdown('help')}
        />

        <ProfileDropdown
          isOpen={dropdownState.menu}
          isPro={isPro}
          avatar={avatar}
          namespace={namespace}
          profileActions={profileActions}
          onToggle={() => toggleDropdown('menu')}
          onClose={() => closeDropdown('menu')}
        />
      </Flex>
    </S.Header>
  );
};

export default Header;
